import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import styles from "../../showroomForm.css";
import Icon from "../../../../../design-system/components/Icon/Icon.jsx";
import { fileToWebP } from "../../../../../lib/images.jsx";

/**
 * A functional React component for rendering a file input field with validation and error handling.
 * Input field is created with react dropzone - it enables file selection via drag & drop or opening the file dialog.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.fieldKey - The unique key used to identify the file input in the form data.
 * @param {string} props.label - The label describing the file input field for accessibility.
 * @param {Function} props.setFormData - A function to update the form data object with the selected file.
 * @param {string} [props.accept] - A string defining the types of files that can be selected, formatted as a file input's `accept` attribute value.
 * @param {Function} [props.validateFile] - A callback function for validating the selected file. Receives `fieldKey` and the selected `file` as arguments.
 * @param {string} [props.error] - An optional error message to display below the file input if validation fails.
 * @returns {JSX.Element} The rendered file input field with error message if applicable.
 */
export const SelectFile = ({
  fieldKey,
  label,
  setFormData,
  accept,
  validateFile,
  error,
}) => {
  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];

      // Set file to uncompressed file for now.
      setFormData((previousFormData) => ({
        ...previousFormData,
        [fieldKey]: file,
      }));

      let processedFile = file;
      if (
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/gif"
      ) {
        processedFile = await fileToWebP(file);

        setFormData((formData) => ({
          ...formData,
          [fieldKey]: processedFile,
        }));
      }

      if (validateFile) validateFile(fieldKey, processedFile);
    },
    [fieldKey, setFormData, validateFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  return (
    <>
      <div
        {...getRootProps({
          className: styles.droppable({ over: isDragActive }),
        })}
      >
        <input
          {...getInputProps({
            name: fieldKey,
            accept: accept,
            "aria-label": label,
            "aria-describedby": error ? `${fieldKey}-error` : undefined,
          })}
        />
        <Icon size={"36px"} name={"landscape"} />
        <p>Datei hierher bewegen oder auswählen</p>
      </div>
    </>
  );
};
